import Loading from '@/common/atoms/Loading'
import TextError from '@/common/atoms/TextError'
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  UseModalProps,
  VStack,
} from '@chakra-ui/react'
import {
  Member_Role_Enum,
  useAdminOrgMembersQuery,
  useAdminSetOrgOwnerMutation,
} from '@gql'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props extends UseModalProps {
  org: { id: string; name: string }
  onUpdated?(): void
}

/**
 * Transfert de propriété (ADM-D5).
 *
 * ✅ Mesuré le 15/08 : PLUSIEURS `Owner` sont possibles. Aucune contrainte ne
 * l'interdit — la seule unicité porte sur `orgId + userId` — et le cas existe
 * déjà en base (« Cercle d'Ancrage UdN » en a deux). Le transfert AJOUTE donc un
 * propriétaire sans rétrograder l'ancien, ce qui tombe juste avec ADM-D5 : Yohan
 * crée des organisations pour ses clients et doit rester capable de dépanner.
 */
export default function AdminOrgOwnerModal({
  org,
  onUpdated,
  ...modalProps
}: Props) {
  const { t } = useTranslation()
  const [memberId, setMemberId] = useState('')

  // Ne charger qu'à l'ouverture : la modale est montée par ligne du tableau.
  const { data, loading, error } = useAdminOrgMembersQuery({
    variables: { orgId: org.id },
    skip: !modalProps.isOpen,
  })

  const [setOrgOwner, { loading: saving }] = useAdminSetOrgOwnerMutation()

  /**
   * TOUS les membres sont listés, propriétaires actuels compris.
   *
   * 🔑 Les masquer était une erreur, remontée par Yohan le 16/08 : « je ne peux
   * pas me mettre propriétaire, je suis automatiquement exclu ». Une liste où
   * l'on ne se trouve pas ne dit pas si l'on est déjà propriétaire ou si l'on
   * n'est pas membre — deux situations opposées, un seul silence. Les
   * propriétaires apparaissent donc, marqués comme tels et non sélectionnables :
   * les désigner à nouveau n'aurait aucun effet, mais les voir répond à la
   * question qu'on se pose en ouvrant cette fenêtre.
   */
  const members = data?.member ?? []
  const hasCandidate = members.some((m) => m.role !== Member_Role_Enum.Owner)

  const handleSubmit = async () => {
    if (!memberId) return
    await setOrgOwner({ variables: { memberId } })
    setMemberId('')
    onUpdated?.()
    modalProps.onClose()
  }

  return (
    <Modal size="lg" {...modalProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('SuperAdmin.orgs.ownerModal.heading', { org: org.name })}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading && <Loading active center />}
          {error && <TextError error={error} />}

          {data && (
            <VStack spacing={4} align="stretch">
              {!hasCandidate ? (
                <Alert status="info">
                  <AlertIcon />
                  {t('SuperAdmin.orgs.ownerModal.noCandidate')}
                </Alert>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>
                      {t('SuperAdmin.orgs.ownerModal.newOwner')}
                    </FormLabel>
                    <Select
                      value={memberId}
                      onChange={(e) => setMemberId(e.target.value)}
                      placeholder={t('SuperAdmin.orgs.ownerModal.choose')}
                    >
                      {members.map((member) => {
                        const isOwner = member.role === Member_Role_Enum.Owner
                        return (
                          <option
                            key={member.id}
                            value={member.id}
                            disabled={isOwner}
                          >
                            {member.name}
                            {member.user?.email && ` — ${member.user.email}`}
                            {isOwner &&
                              ` (${t(
                                'SuperAdmin.orgs.ownerModal.alreadyOwner'
                              )})`}
                          </option>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <Text fontSize="sm" color="gray.500">
                    {t('SuperAdmin.orgs.ownerModal.info')}
                  </Text>
                </>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button onClick={modalProps.onClose} mr={3}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!memberId}
            isLoading={saving}
            onClick={handleSubmit}
          >
            {t('SuperAdmin.orgs.ownerModal.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
